import { ApiPath } from '@common/app/api-path';
import {
  type ApplyChestRequestType,
  type ApplyChestResponseType,
  type ContractData,
  type ContractDataType,
  type GetContractsResponseType,
  type NewContractResponseType,
  type OpenChestResponseType,
  type OpenContractRequestType,
  type ProductOfDayOpenChestboxResponse,
  type ResponseContractDataType,
  type UpgradeProductOfDayTelegramStarsResponseType,
} from '@common/types/contract';

import { getEndTimeFromNow } from '@/common/lib/date';
import {
  type ActivateVipStatusBuyLinkResponseType,
  type ActivateVipStatusResponseType,
  type TONTransactionRequestDataType,
} from '@/common/types/query/user';

import { api } from '../base';

import { completeOrRejectContractHandler } from './query-started-handler';

const transformResponse = (
  response: GetContractsResponseType,
): GetContractsResponseType => ({
  ...response,
  contracts: response.contracts.map((contract) => ({
    ...contract,
    openedEndAt: contract.timeLeft
      ? getEndTimeFromNow(contract.timeLeft)
      : undefined,
  })),
});

const contractApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getContracts: builder.query<GetContractsResponseType, void>({
      query: () => ApiPath.GET_CONTRACTS,
      transformResponse,
      keepUnusedDataFor: 0,
    }),
    completeContract: builder.mutation<
      GetContractsResponseType,
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: ApiPath.GET_CONTRACTS + `/${contractId}/complete`,
        method: 'POST',
      }),
      onQueryStarted: completeOrRejectContractHandler('done'),
      transformResponse,
    }),
    buyExtendTime: builder.mutation<ResponseContractDataType, { id: string }>({
      query: ({ id }) => ({
        url: ApiPath.GET_CONTRACTS + `/${id}/extend-timer`,
        method: 'POST',
        transformResponse,
      }),
    }),
    rejectContract: builder.mutation<
      GetContractsResponseType,
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: ApiPath.GET_CONTRACTS + `/${contractId}/reject`,
        method: 'POST',
      }),
      onQueryStarted: completeOrRejectContractHandler('reject'),
      transformResponse,
    }),
    newContract: builder.mutation<
      NewContractResponseType,
      { contractId: string }
    >({
      query: ({ contractId }) => ({
        url: ApiPath.GET_CONTRACTS + `/${contractId}/new`,
        method: 'POST',
      }),
    }),
    openChest: builder.query<OpenChestResponseType | null, void>({
      query: () => ApiPath.OPEN_CHEST,
      keepUnusedDataFor: 0,
    }),
    applyChest: builder.mutation<ApplyChestResponseType, ApplyChestRequestType>(
      {
        query: ({ id, adsView }) => ({
          url: ApiPath.APPLY_CHEST + id,
          method: 'POST',
          body: { adsView },
        }),
      },
    ),
    openContract: builder.mutation<
      | GetContractsResponseType
      | void
      | ActivateVipStatusResponseType
      | ActivateVipStatusBuyLinkResponseType,
      OpenContractRequestType
    >({
      query: ({ contractId, type, currency }) => ({
        url: ApiPath.GET_CONTRACTS + `/open/${contractId}`,
        method: 'POST',
        body: { type, currency },
      }),
      transformResponse: (
        response:
          | GetContractsResponseType
          | void
          | ActivateVipStatusResponseType
          | ActivateVipStatusBuyLinkResponseType,
      ) => {
        if (response && 'contracts' in response) {
          return transformResponse(response);
        } else {
          return response;
        }
      },
    }),
    shufleCustomContract: builder.mutation<
      ContractDataType,
      { data: ContractData }
    >({
      query: ({ data }) => ({
        url: ApiPath.GET_CONTRACTS + '/custom/shufle',
        method: 'POST',
        body: { ...data },
      }),
    }),
    upgradeProductOfDay: builder.mutation<
      | void
      | UpgradeProductOfDayTelegramStarsResponseType
      | TONTransactionRequestDataType,
      { slot: number; currency: 'xtr' | 'ton' | 'usdt' }
    >({
      query: ({ slot, currency }) => ({
        url: '/user/contract/product-of-day/upgrade',
        method: 'POST',
        body: { slot, currency },
      }),
    }),
    openProductOfDayChestbox: builder.mutation<
      ProductOfDayOpenChestboxResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/user/contract/product-of-day/open-chestbox/${id}`,
        method: 'POST',
      }),
    }),
  }),
});

const {
  useLazyGetContractsQuery,
  useCompleteContractMutation,
  useRejectContractMutation,
  useLazyOpenChestQuery,
  useApplyChestMutation,
  useGetContractsQuery,
  useNewContractMutation,
  useOpenContractMutation,
  useShufleCustomContractMutation,
  useUpgradeProductOfDayMutation,
  useOpenProductOfDayChestboxMutation,
  useBuyExtendTimeMutation,
} = contractApi;

export {
  contractApi,
  useApplyChestMutation,
  useBuyExtendTimeMutation,
  useCompleteContractMutation,
  useGetContractsQuery,
  useLazyGetContractsQuery,
  useLazyOpenChestQuery,
  useNewContractMutation,
  useOpenContractMutation,
  useOpenProductOfDayChestboxMutation,
  useRejectContractMutation,
  useShufleCustomContractMutation,
  useUpgradeProductOfDayMutation,
};
